import React, { useEffect, useState } from "react";
import { FileDown, Save, Trash2, LayoutTemplate } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/documents/RichTextEditor";
import { RevisionHistory } from "@/components/documents/RevisionHistory";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

export const WRITTEN_TYPES = ["Policy", "Procedure", "Work Instruction", "Manual", "Form"];
const EMPTY = { client_slug: "", title: "", doc_type: "Procedure", doc_number: "", revision: "1", authorised_by: "", authorised_date: "", effective_date: "", revision_date: "", prepared_by: "", content_html: "", revision_history: [] };

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>
);

const MetaFields = ({ form, set, clients, isNew }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 min-w-0">
    <Field label="Client">
      <Select value={form.client_slug} onValueChange={(v) => set("client_slug", v)} disabled={!isNew}>
        <SelectTrigger data-testid="wd-client-select" className="h-9"><SelectValue placeholder="Select client" /></SelectTrigger>
        <SelectContent>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
    <Field label="Type">
      <Select value={form.doc_type} onValueChange={(v) => set("doc_type", v)}>
        <SelectTrigger data-testid="wd-type-select" className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{WRITTEN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
    <div className="sm:col-span-2"><Field label="Document name"><Input data-testid="wd-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Control of Documented Information" className="h-9" /></Field></div>
    <Field label="Document number"><Input data-testid="wd-number-input" value={form.doc_number} onChange={(e) => set("doc_number", e.target.value)} placeholder={isNew ? "Auto if left blank" : ""} className="h-9" /></Field>
    <Field label="Revision"><Input data-testid="wd-revision-input" value={form.revision} onChange={(e) => set("revision", e.target.value)} className="h-9" /></Field>
    <Field label="Authorised by"><Input data-testid="wd-authorised-input" value={form.authorised_by} onChange={(e) => set("authorised_by", e.target.value)} placeholder="Name and title" className="h-9" /></Field>
    <Field label="Date authorised"><Input data-testid="wd-authorised-date-input" type="date" value={form.authorised_date} onChange={(e) => set("authorised_date", e.target.value)} className="h-9" /></Field>
    <Field label="Effective date"><Input data-testid="wd-effective-date-input" type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="h-9" /></Field>
    <Field label="Date of last revision"><Input data-testid="wd-revision-date-input" type="date" value={form.revision_date} onChange={(e) => set("revision_date", e.target.value)} className="h-9" /></Field>
  </div>
);

export default function WrittenDocEditor({ open, docId, clients, defaultClientSlug, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [id, setId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadKey, setLoadKey] = useState(0);
  const [templates, setTemplates] = useState(null);
  const [me, setMe] = useState("");

  useEffect(() => {
    if (open && !templates) {
      endpoints.writtenDocTemplates().then(setTemplates).catch(() => setTemplates({}));
      endpoints.me().then((u) => setMe(u.name || "")).catch(() => {});
    }
  }, [open, templates]);


  useEffect(() => {
    if (!open) return;
    setId(docId || null);
    if (docId) {
      endpoints.getWrittenDoc(docId).then((d) => { setForm({ ...EMPTY, ...d }); setLoadKey((k) => k + 1); }).catch(() => toast.error("Could not open document"));
    } else {
      setForm({ ...EMPTY, client_slug: defaultClientSlug || (clients.length === 1 ? clients[0].slug : "") });
      setLoadKey((k) => k + 1);
    }
  }, [open, docId, defaultClientSlug, clients]);

  useEffect(() => {
    if (open && !docId && templates) {
      setForm((f) => (f.content_html ? f : { ...f, content_html: templates[f.doc_type] || "" }));
      setLoadKey((k) => k + 1);
    }
  }, [open, docId, templates]);

  const set = (k, v) => setForm((f) => {
    if (k === "doc_type" && !id && templates && (!f.content_html || f.content_html === templates[f.doc_type])) {
      setLoadKey((n) => n + 1);
      return { ...f, doc_type: v, content_html: templates[v] || "" };
    }
    return { ...f, [k]: v };
  });

  const loadTemplate = () => {
    if (!templates?.[form.doc_type]) { toast.error("No template for this type"); return; }
    if (form.content_html && !window.confirm(`Replace the current text with the ${form.doc_type} template?`)) return;
    setForm((f) => ({ ...f, content_html: templates[f.doc_type] }));
    setLoadKey((n) => n + 1);
  };

  const save = async () => {
    if (!form.client_slug || !form.title.trim()) { toast.error("Choose a client and enter a document name"); return null; }
    setBusy(true);
    try {
      const payload = { title: form.title, doc_type: form.doc_type, doc_number: form.doc_number, revision: form.revision,
        authorised_by: form.authorised_by, authorised_date: form.authorised_date, effective_date: form.effective_date,
        revision_date: form.revision_date, revision_history: form.revision_history, content_html: form.content_html };
      const d = id ? await endpoints.updateWrittenDoc(id, payload) : await endpoints.createWrittenDoc({ ...payload, client_slug: form.client_slug });
      setId(d.id); setForm((f) => ({ ...f, doc_number: d.doc_number }));
      toast.success("Document saved"); onSaved?.();
      return d.id;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); return null; } finally { setBusy(false); }
  };

  const exportWord = async () => {
    const savedId = await save();
    if (!savedId) return;
    try { await endpoints.exportWrittenDoc(savedId); } catch { toast.error("Could not export"); }
  };

  const remove = async () => {
    if (!id || !window.confirm("Delete this document permanently?")) return;
    try { await endpoints.deleteWrittenDoc(id); toast.success("Document deleted"); onSaved?.(); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wd-editor-dialog" className="max-w-5xl w-[96vw] max-h-[94vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle className="font-display">{id ? "Edit document" : "Write a new procedure or policy"}</DialogTitle>
          <DialogDescription>New documents start from your ISOQuest template. On export the client logo, document name, number, revision, dates, authoriser and revision history are filled into the Word layout automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          <MetaFields form={form} set={set} clients={clients} isNew={!id} />
          <RevisionHistory rows={form.revision_history || []} onChange={(rows) => set("revision_history", rows)} currentRev={form.revision} me={me} />
          <div className="flex items-center">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Content</span>
            <Button type="button" size="sm" variant="ghost" className="ml-auto h-7" onClick={loadTemplate} data-testid="wd-load-template-button">
              <LayoutTemplate className="h-3.5 w-3.5 mr-1" /> Load {form.doc_type} template
            </Button>
          </div>
          <RichTextEditor value={form.content_html} onChange={(h) => set("content_html", h)} docKey={loadKey} />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {id && <Button type="button" variant="ghost" className="text-rose-600" onClick={remove} data-testid="wd-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="wd-close-button">Close</Button>
            <Button type="button" variant="outline" onClick={exportWord} disabled={busy} data-testid="wd-export-button"><FileDown className="h-4 w-4 mr-1" /> Save & export Word</Button>
            <Button type="button" className="brand-gradient text-white" onClick={save} disabled={busy} data-testid="wd-save-button"><Save className="h-4 w-4 mr-1" /> {busy ? "Saving..." : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
